import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { InputDialogData, InputDialogComponent, InputDialogReturnData, InputDialogReturnType, InputDialogSelection } from './input.dialog.component';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ConfirmDeleteData, openConfirmDeleteGetResult } from 'src/app/components/dialog.confirmDelete.component';
import { JoiInputComponent } from '../joi-input/joi-input.component';
import { timeHelper } from '../../../../globals/helpers/time.helper';
import { defaultDialogData, DialogWrapperComponent } from '../wrappers/dialog-wrapper.component';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToggleButtonWrapperComponent } from '../buttons/toggle-button-wrapper.component';
import { TitleIconComponent } from '../icon/title-icon.component';
import { SearchBarComponent } from '../search-bar';
import { MatInputModule } from '@angular/material/input';

// Creating this component to make the input dialog more flexible for slightly more complex single input dialogs, like the create program dialog which has additional decorations around the input.  
@Component({
    selector: 'input-dialog-body',
    imports: [MatFormFieldModule, JoiInputComponent, SearchBarComponent, MatInputModule, FormsModule, CommonModule, DialogWrapperComponent, TitleIconComponent, ToggleButtonWrapperComponent, ],// SearchBarComponent, JoiInputComponent,
    template: `
<dialog-wrapper
  [backgroundColor]="data.backgroundColor"
  [disabled]="(!data.emptyOK && !data.value) || (!!data.requireSelection && !selection)"
  [disableAll]="(!data.emptyOK && !data.value) || (!!data.requireSelection && !selection)"
  [successLabel]="data.successLabel"
  [dialogTitle]="data.title"
  [smallTitle]="!!smallTitle || !!data.small"
  [tiny]="!!data.small"
  [giveMeTwenty]="!!data.small"
  [titleHeight]="smallTitle || data.small ? 40 : 60"
  [cancelLeft]="!data.hideCancel"
  [showClose]="!!data.hideCancel"
  [midLeftLabel]="data.alternativeLabel"
  [midRightLabel]="data.midRightLabel"
  (onMidRight)="onMidRight.emit()"
  [enableMidLeft]="!!data.alternativeEmptyOK"
  [maxHeight]="data.maxHeight || 'unset'"
  [deleteLeft]="!!data.delete"
  [data]="data.position ? { position: data.position, dialogRef: dialogRef } : defaultDialogData()"
  (cancel)="cancel()"
  (close)="cancel()"
  (submit)="submit()"
  (onMidLeft)="onAlternative()"
  (delete)="delete($event)"
>
  <div *ngIf="data.header" class="para">
    {{ data.header }}
  </div>
  <ng-content select=".aboveInput"></ng-content>
  <mat-form-field class="general" *ngIf="!data.multiLine">
    <input matInput name="value" (keyup.enter)="data.secondPlaceholder ? null : submit()" [(ngModel)]="data.value" [placeholder]="data.placeholder">
  </mat-form-field>
  <mat-form-field class="general" *ngIf="!!data.multiLine">
    <textarea matInput cdkTextareaAutosize name="value" (keyup.enter)="data.secondPlaceholder ? null : submit()" [(ngModel)]="data.value" [placeholder]="data.placeholder"></textarea>
  </mat-form-field>
  <mat-form-field class="general" *ngIf="!!data.secondPlaceholder">
    <textarea matInput cdkTextareaAutosize name="secondValue" [(ngModel)]="data.secondValue" [placeholder]="data.secondPlaceholder"></textarea>
  </mat-form-field>
  <ng-content select=".belowInput"></ng-content>
  <title-icon *ngIf="!!time" [titleIcon]="'time24'">
    <joi-input #timeInput class="fit-content" [placeholder]="'00:00'" [fixedWidth]="true" [autoSelect]="true" [(model)]="time" [maxlength]="7" (joiChange)="updateTime()"></joi-input>
  </title-icon>
  <div class="selections" *ngIf="data.requireSelection">
    <h5>ADD TASK TO:</h5>
    <search-bar (keyword)="search($event)" [debounceTime]="100" [gray]="true"></search-bar>
    <toggle-button [selected]="s.id === selection" *ngFor="let s of selections;" (select)="select(s)">
      <div class="name">{{ s.name }}</div>
      <div class="grey-text" [ngClass]="{ selected: s.id === selection }">{{ s.greyText }}</div>
    </toggle-button>
</div>
</dialog-wrapper>
  `,
    styles: [`
    .para { margin-bottom: 15px; display:flex; }
    .general { display: flex; align-items: center; padding-bottom: 10px; }
    .grey-text { color: rgba(0,0,0,0.4); font-size: 12px; }
    .grey-text.selected { color: #ddd; }
    .selections { margin-bottom: 20px; }
    search-bar { display: block; margin: 10px 0px; }
  `],
    standalone: true,
})
export class InputDialogBodyComponent implements OnInit {
  @Input() data: InputDialogData
  @Input() smallTitle = false
  @Input() manualSubmit = false
  @Input() dialogRef: MatDialogRef<any> | { close: (data: InputDialogReturnData) => null }
  @Output() onMidRight: EventEmitter<null> = new EventEmitter()
  @Output() onSubmit: EventEmitter<null> = new EventEmitter()
  time: string

  @ViewChild('timeInput') timeInput: JoiInputComponent
  @ViewChild(DialogWrapperComponent) dialogWrapper: DialogWrapperComponent
  cachedSelections: InputDialogSelection[] = []
  selections: InputDialogSelection[] = []
  selection: string
  defaultDialogData = defaultDialogData

  constructor(private dialog: MatDialog) {} 

  select(selection: InputDialogSelection) {
    this.selection = selection.id
  }

  ngOnInit() {
    const { time, selections } = this.data
    if (time) this.time = time
    if (selections) this.cachedSelections = selections
    this.selections = selections
  }

  search(term: string) {
    term = term.toLocaleLowerCase()
    this.selections = this.cachedSelections.filter(s => s.name.toLocaleLowerCase().includes(term))
    if (this.selection && !this.selections.find(s => s.id === this.selection)) this.selection = null
  }

  cancel() {
    this.dialogRef.close(null)
  }

  async updateTime() {
    const parsedTime = timeHelper.parseTime(this.time)
    this.time = parsedTime
    setTimeout(() => this.timeInput.setModel(parsedTime))
  }

  async delete(event) {
    const rect = event.target.getBoundingClientRect();
    const data: ConfirmDeleteData = {
      title: this.data.deleteTitle,
      para: this.data.deleteText,
      deleteButtonText: this.data.deleteButtonText,
      rect,
      dialog: this.dialog
    }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return
    const returnData: InputDialogReturnData = { type: 'Delete' }
    this.dialogRef.close(returnData)

  }

  sendValues(type: InputDialogReturnType) {
    const { value, emptyOK, alternativeEmptyOK,  secondValue, secondPlaceholder, requireSelection } = this.data
    const returnData: InputDialogReturnData = { type, values: [value, secondValue] }
    if (requireSelection) returnData.selection = this.selection

    if (this.data.time) returnData.time = this.time
    if (value || emptyOK || alternativeEmptyOK) this.dialogRef.close(returnData)
  }

  submit() {
    if (this.manualSubmit) this.onSubmit.emit()
    else this.sendValues('MainButton')
  }

  onAlternative() {
    this.sendValues('AlternativeButton')
  }

}